import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Button } from '../ui';
import { ScoreSubmission } from '../highscores';
import { useGame } from '../../stores/game.store';
import { useAuth } from '../../stores/auth.store';

interface GameWrapperProps {
  gameId: string;
  gameName: string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Wrapper component that provides game UI chrome:
 * - Score display
 * - Pause overlay
 * - Game over with score submission
 * - Exit button
 */
export function GameWrapper({
  gameId,
  gameName,
  children,
  className = '',
}: GameWrapperProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    session,
    isGameOver,
    isPaused,
    score,
    level,
    lives,
    endGame,
    togglePause,
  } = useGame();
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  const handleExit = useCallback(() => {
    if (session && !isGameOver) {
      setShowExitConfirm(true);
    } else {
      endGame();
      navigate('/games');
    }
  }, [session, isGameOver, endGame, navigate]);

  const confirmExit = useCallback(() => {
    endGame();
    navigate('/games');
  }, [endGame, navigate]);

  const handleScoreSubmitted = useCallback(() => {
    // Score was submitted successfully
  }, []);

  const handlePlayAgain = useCallback(() => {
    // Reload the page to restart the game
    window.location.reload();
  }, []);

  return (
    <div className={`relative ${className}`}>
      {/* Game HUD */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 flex justify-between items-start pointer-events-none">
        {/* Left: Score & Level */}
        <div className="bg-slate-900/80 backdrop-blur-sm rounded-lg px-4 py-2 pointer-events-auto">
          <div className="text-xs text-slate-500 mb-1">{gameName}</div>
          <div className="text-2xl font-mono text-purple-400">
            {score.toLocaleString()}
          </div>
          <div className="text-xs text-slate-400">
            Level {level} • Lives: {'❤️'.repeat(Math.max(0, lives))}
          </div>
        </div>

        {/* Right: Controls */}
        <div className="flex gap-2 pointer-events-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={togglePause}
            disabled={isGameOver}
          >
            {isPaused ? '▶️ Resume' : '⏸️ Pause'}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleExit}>
            🚪 Exit
          </Button>
        </div>
      </div>

      {/* Game Canvas */}
      <div className="w-full h-full overflow-hidden">{children}</div>

      {/* Pause Overlay */}
      {isPaused && !isGameOver && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <Card className="text-center max-w-sm">
            <CardContent className="py-8">
              <h2 className="text-2xl font-bold text-white mb-4">⏸️ Paused</h2>
              <p className="text-slate-400 mb-6">Press P or ESC to resume</p>
              <div className="space-y-2">
                <Button onClick={togglePause} fullWidth>
                  Resume Game
                </Button>
                <Button variant="outline" onClick={handleExit} fullWidth>
                  Exit Game
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Game Over Overlay */}
      {isGameOver && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="max-w-md w-full mx-4">
            <ScoreSubmission
              gameId={gameId}
              score={score}
              onSubmitted={handleScoreSubmitted}
              onClose={handlePlayAgain}
              autoSubmit={isAuthenticated}
            />
            <div className="mt-4 flex gap-2">
              <Button onClick={handlePlayAgain} fullWidth>
                Play Again
              </Button>
              <Button variant="outline" onClick={handleExit} fullWidth>
                Back to Games
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Exit Confirmation */}
      {showExitConfirm && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70">
          <Card className="max-w-sm">
            <CardContent className="py-6 text-center">
              <h3 className="text-xl font-bold text-white mb-2">Exit Game?</h3>
              <p className="text-slate-400 mb-4">
                Your current progress will be lost.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowExitConfirm(false)}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button variant="danger" onClick={confirmExit} fullWidth>
                  Exit
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
